import { Fragment, type FunctionComponent, type PropsWithChildren } from 'react';
import { HOMEPAGE_LINK } from '../../constants/links';
import { generateDataSelector } from '../../helpers/general-helper/general-helper';
import { StyledLink } from '../common-components/link/styled-link.component';
import ChevronRightIcon from '../svgs/untitled-ui/chevron-right.svg';
import HomeIcon from '../svgs/untitled-ui/home.svg';
import { breadcrumbsContainer } from './breadcrumbs.css';

export type Breadcrumb = {
	name: string;
	url?: string;
};

export type BreadcrumbsProps = {
	breadcrumbs: Breadcrumb[];
	homePageLink?: string; // used for instances when we wish to override the home icon link such as shop by look
	className?: string;
};

type BreadcrumbItemProp = {
	breadcrumb?: Breadcrumb;
	position: string;
	isLast?: boolean;
};

const BreadcrumbItem: FunctionComponent<PropsWithChildren<BreadcrumbItemProp>> = ({
	children,
	position,
	breadcrumb = { name: '' },
	isLast
}) => {
	let breadcrumbElement: React.ReactNode | HTMLSpanElement | null = null;

	if (breadcrumb) {
		if (breadcrumb.url) {
			breadcrumbElement = (
				<StyledLink
					itemProp="item"
					underlineHover={true}
					url={breadcrumb.url}
					title={breadcrumb.name}
					ariaLabel={breadcrumb.name}
					tabIndex={isLast ? -1 : 0}
					aria-disabled={isLast}
					className={`pv0-ns pv2 theme-luxury:no-underline! theme-luxury:hover:underline!${
						isLast ? ' disableClickEvents no-underline!' : ''
					}`}>
					<span
						itemProp="name"
						aria-current={isLast ? 'page' : undefined}
						className={`theme-luxury:md:block! theme-luxury:whitespace-nowrap! theme-luxury:text-primary!${
							isLast ? ' theme-luxury:text-quaternary!' : ' theme-luxury:md:max-w-48 theme-luxury:md:truncate'
						}`}>
						{breadcrumb.name}
					</span>
				</StyledLink>
			);
		} else {
			breadcrumbElement = (
				<span itemProp="item" className="theme-luxury:text-quaternary!">
					<span itemProp="name" aria-current="page">
						{breadcrumb.name}
					</span>
				</span>
			);
		}
	}

	return (
		<li
			itemScope={true}
			itemProp="itemListElement"
			itemType="https://schema.org/ListItem"
			data-automation={generateDataSelector('breadcrumb', breadcrumb?.name)}>
			{breadcrumbElement}
			<meta itemProp="position" content={position} />
			{children}
		</li>
	);
};

export const Breadcrumbs: FunctionComponent<BreadcrumbsProps> = ({ breadcrumbs, homePageLink, className = '' }) => {
	if (!breadcrumbs || !breadcrumbs.length) {
		return;
	}

	return (
		<nav aria-label="Breadcrumb" className={className}>
			<ol
				className={`h-auto-ns h2 flex flex-wrap list ma0 pa0 f6 items-center theme-luxury:max-md:flex-nowrap! theme-luxury:max-md:overflow-y-scroll! theme-luxury:max-md:whitespace-nowrap! ${breadcrumbsContainer}`}
				itemScope={true}
				itemType="https://schema.org/BreadcrumbList">
				<BreadcrumbItem position="1">
					<StyledLink
						className="theme-luxury:button-utility! small"
						itemProp="item"
						url={homePageLink ? homePageLink : HOMEPAGE_LINK}
						ariaLabel="Return to Home">
						<HomeIcon className="f4 theme-luxury:fg-quaternary!" data-testid="HomeIcon" />
						<meta itemProp="name" content="Home" />
					</StyledLink>
				</BreadcrumbItem>
				{breadcrumbs.map((breadcrumb, index) => (
					<Fragment key={breadcrumb.name}>
						<span className="mt1" aria-hidden={true}>
							<ChevronRightIcon
								className="theme-grey-light text-xl pb-0.5 theme-luxury:text-base! theme-luxury:pb-0"
								data-testid="ChevronRightIcon"
							/>
						</span>
						<BreadcrumbItem
							position={(index + 2).toString()}
							breadcrumb={breadcrumb}
							isLast={index + 1 === breadcrumbs.length}
						/>
					</Fragment>
				))}
			</ol>
		</nav>
	);
};
